import moment from "moment";
import exceljs from "exceljs";
import { ROUTES } from "../../../routes";
import { useContext, useEffect, useState } from "react";
import { Dropdown, Spinner } from "react-bootstrap";
import ApiCohort from "../../../modules/api/cohort";
import ApiExport from "../../../modules/api/export";
import ModulePopup from "../../../modules/popup/popup";
import usePageTitle from "../../../hooks/use-page-title";
import Notifier from "../../../modules/notifier/notifier";
import Debouncer from "../../../modules/debouncer/debouncer";
import AppUserContext from "../../../context/app-user-context";
import { PortalContext, usePortalSettingSet } from "../../../context/portal-context";
import { MdSearch, MdModeEditOutline } from "react-icons/md";
import { SUBMISSION_WL, SUBMISSION_PD } from "../../../config/constants";
import PagePortalNotFound from "../PagePortalNotFound/PagePortalNotFound";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import ServerSideCohortDataTable from "../../../components/common/ServerSideCohortDataTable";
import FooterBoxAction from "../../../components/common/FooterBoxAction";
import TextEllipsis from "../../../components/dumb/TextEllipsis";

export default function PageExportReportCohortList() {
  const title = "Export Reports";
  usePageTitle(title);
  usePortalSettingSet("label_header", title);
  const { setCustomText } = useContext(PortalContext);
  const user = useContext(AppUserContext);
  const { programmeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [programme, setProgramme] = useState<any>(null);
  const [refresh, setRefresh] = useState(false);
  const [search, setSearch] = useState("");
  const [orderBy, setOrderBy] = useState("endDate");
  const [orderDirection, setOrderDirection] = useState("desc");
  const [selectedCohorts, setSelectedCohorts] = useState<any[]>([]);
  const [exporting, setExporting] = useState({
    Summary: false,
    [SUBMISSION_WL]: false,
    [SUBMISSION_PD]: false,
  });

  useEffect(() => {
    setCustomText(title);
  }, []);

  const back = () => {
    navigate(location.state?.from ?? ROUTES.PORTAL_DASHBOARD);
  };

  const onSort = (column: any, sortDirection: string, _) => {
    setOrderBy(column.id);
    setOrderDirection(sortDirection);
  };

  const exportData = async (cohort: any, type: string = null) => {
    if (Object.keys(exporting).some((key) => exporting[key])) return;
    setExporting((exporting) => ({ ...exporting, [type ?? "Summary"]: true }));
    await ApiExport.exportCohort(cohort._id, type)
      .then((res) => {
        if (res.status) {
          exportExcel(res.data, type);
        } else {
          const message = `Unable to export. ${res.message ?? ``}`;
          Notifier.error(message);
        }
      })
      .catch((error) => Notifier.error(error.message));
    setExporting((exporting) => ({ ...exporting, [type ?? "Summary"]: false }));
  };

  const exportExcel = (data: any, type: string) => {
    if (typeof data !== "object" || !data) {
      Notifier.error("There is no data to export for this cohort");
      return;
    }
    const cohort = data.cohort;
    const wlDates = Array.isArray(data.wlDates) ? data.wlDates : [];
    const pdDates = Array.isArray(data.pdDates) ? data.pdDates : [];
    const students = Array.isArray(data.students) ? data.students : [];
    const dates = [...wlDates, ...pdDates];
    const columns = getColumns(dates, type);
    const rows = getRows(cohort, dates, students, type);

    const workbook = new exceljs.Workbook();
    const worksheet = workbook.addWorksheet("Data");
    worksheet.addTable({
      name: "Data",
      ref: "A1",
      headerRow: true,
      style: { theme: "TableStyleMedium9", showRowStripes: true },
      columns,
      rows,
    });
    formatColumns(worksheet, columns);
    formatCells(worksheet, columns, rows, type);
    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `GROW-Pro ${type ?? "Summary"} Report ${moment().format(
        "YYYY-MM-DD"
      )} - ${programme.name} - ${cohort.name}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  const getColumns = (dates: any[], type: string): any[] => {
    let columns: any[] = [
      { name: "Student Name", width: 134 },
      { name: "Student Email", width: 320 },
      { name: "Programme Name", width: 145 },
      { name: "Cohort Name", width: 172 },
      { name: "Employer", width: 134 },
      { name: "Job Title", width: 134 },
      { name: "SIT Supervisor Name", width: 192 },
    ];

    if (type === SUBMISSION_WL) {
      columns.push({ name: "SIT Supervisor Email", width: 320 });
      columns.push({ name: "Industry Supervisor Name", width: 225 });
      columns.push({ name: "Industry Supervisor Email", width: 320 });
    } else {
      columns.push({ name: "Industry Supervisor Name", width: 225 });
    }
    dates.forEach((date: any) => {
      let dateTo = moment(date.to).format("DD-MMM-YYYY");
      let dateType = "";
      date.type === SUBMISSION_WL ? (dateType = "WLR") : (dateType = "PDR");
      columns.push({
        name: `${dateType} (${dateTo})`.trim(),
        width: 160,
      });
      if (type === SUBMISSION_WL) {
        columns.push({
          name: `${dateType} Submission (${dateTo})`.trim(),
          width: 230,
          alignment: { wrapText: true },
        });
        columns.push({
          name: `${dateType} Feedback (${dateTo})`.trim(),
          width: 230,
        });
      }
      columns.push({
        name: `${dateType} Score (${dateTo})`.trim(),
        width: 190,
        alignment: { horizontal: "left" },
      });
    });
    return columns.map((column) => ({
      ...column,
      alignment: { vertical: "top", ...column.alignment },
      filterButton: true,
    }));
  };

  const getRows = (
    cohort: any,
    dates: any[],
    students: any[],
    type: string
  ) => {
    let rows = students.map((student) => {
      let row = [
        student.name,
        student.email,
        programme.name,
        cohort.name,
        student.organization,
        student.jobTitle
      ];
      let sitSupervisors = Array.isArray(student.sitSupervisors)
        ? student.sitSupervisors
        : [];
      let industrySupervisors = Array.isArray(student.industrySupervisors)
        ? student.industrySupervisors
        : [];
      row.push(sitSupervisors.map((_: any) => _.name).join(";"));
      if (type === SUBMISSION_WL) {
        row.push(sitSupervisors.map((_: any) => _.email).join(";"));
        row.push(industrySupervisors.map((_: any) => _.name).join(";"));
        row.push(industrySupervisors.map((_: any) => _.email).join(";"));
      } else {
        row.push(industrySupervisors.map((_: any) => _.name).join(";"));
      }
      dates.forEach((date) => {
        const submissions =
          date.type === SUBMISSION_WL
            ? Array.isArray(student.wlSubmissions)
              ? student.wlSubmissions
              : []
            : Array.isArray(student.pdSubmissions)
            ? student.pdSubmissions
            : [];
        const feedbacks = Array.isArray(student.feedbacks)
          ? student.feedbacks
          : [];
        const submission = submissions.find(
          (s: any) => s.submissionDateId === date._id
        );
        const submittedDate =
          submission && submission.createdDate
            ? moment(submission.createdDate).format("DD-MMM-YYYY")
            : null;
        const feedback = submission
          ? feedbacks.find(
              (feedback: any) => feedback.submissionId === submission._id
            )
          : null;
        row.push(submittedDate);
        if (type === SUBMISSION_WL) {
          const content = submission
            ? getContent(submission, cohort.wlFormat?.fields)
            : null;
          row.push(content);
          row.push(feedback?.message);
        }
        row.push(feedback?.score);
      });
      return row;
    });
    return rows.length ? rows : [[]];
  };

  const getContent = (submission: any, fields?: any[]) => {
    let richText = [];
    let content = null;
    try {
      content = JSON.parse(submission.content);
      if (typeof content !== "object") content = null;
    } catch {
      content = null;
    }
    if (!content || !fields || !Array.isArray(fields)) {
      richText = [
        { text: "Summary of Key Activities\n", font: { bold: true } },
        { text: `${getPlainText(submission.summaryKeyActivities)}\n` },
        { text: "Details of Learning\n", font: { bold: true } },
        { text: `${getPlainText(submission.detailsLearning)}\n` },
        { text: "Reflection\n", font: { bold: true } },
        { text: `${getPlainText(submission.reflection)}\n` },
      ];
    } else {
      fields.forEach((field) => {
        richText.push({ text: `${field.name}\n`, font: { bold: true } });
        let value = content[field._id];
        richText.push({ text: `${getPlainText(value)}\n` });
        if (value) richText.push({ text: "\n" });
      });
    }
    return { richText };
  };

  const getPlainText = (html: string) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const formatColumns = (worksheet: any, columns: any[]) => {
    columns.forEach((column, i) => {
      if (column.width && Number.isInteger(column.width)) {
        const width = column.width >= 5 ? (column.width - 5) / 7 : 10;
        worksheet.getColumn(i + 1).width = width;
      }
    });
  };

  const formatCells = (
    worksheet: any,
    columns: any[],
    rows: any[][],
    type: string
  ) => {
    for (let rowIndex = 2; rowIndex <= rows.length + 1; rowIndex++) {
      columns.forEach((column, i) => {
        let row = worksheet.getRow(rowIndex);
        if (row && type === SUBMISSION_WL) row.height = 100;
        let cell = row?.getCell(i + 1);
        if (cell) {
          if (column.numFmt) cell.numFmt = column.numFmt;
          if (column.alignment) cell.alignment = column.alignment;
        }
      });
    }
  };

  if (!user.programmeIds.includes(programmeId)) {
    return <PagePortalNotFound />;
  }
  return (
    <main className="form-generic content-generic content-generic--fluid">
      <h1 className="fs-title hide-mobile-down" style={{ marginBottom: 15 }}>
        {/* Export Reports */}
        Programme: {programme && <span>{programme.name}</span>}
      </h1>
      {/* <div className="programme-title d-none" style={{ marginBottom: 15 }}>
        Programme: {programme && <span>{programme.name}</span>}
      </div> */}
      <div className="cg__actions">
        <div className="cg__action-buttons d-none">
          {/* <button className="btn btn-primary" onClick={back}>
            <HiArrowLeft /> Back
          </button> */}
          {/* <Link
            to={`${ROUTES.PORTAL_COHORT_ADD}/${programmeId}`}
            className="btn btn-primary"
            state={location.state}
          >
            Add New Cohort
          </Link> */}
          <button
            className="btn btn-outline-dark d-none"
            onClick={() => setRefresh((i) => !i)}
          >
            Refresh
          </button>{" "}
          {selectedCohorts.length > 0 && (
            <button
              className="btn btn-outline-primary"
              onClick={(e) => {
                const deletedIds = selectedCohorts
                  .filter((i) => typeof i._id !== "undefined")
                  .map((i) => i._id);
                ModulePopup.showAlertConfirm({
                  title: "Delete Confirmation",
                  description: (
                    <>
                      <p>
                        Are you sure to delete {deletedIds.length} cohorts?
                        <br /> This action can't be undone.
                      </p>
                    </>
                  ),
                  onYes: () =>
                    Debouncer.execute("DELETE_COHORTS", async () => {
                      await ApiCohort.deleteByIds(deletedIds)
                        .then((res) => {
                          if (res.result === "OK") {
                            setRefresh((i) => !i);
                            setSelectedCohorts([]);
                            Notifier.success(res.message);
                          } else {
                            Notifier.error(res.message);
                          }
                        })
                        .catch((error) => {
                          Notifier.error(error.message);
                        });
                    }),
                });
              }}
            >
              Delete Selected
            </button>
          )}
        </div>
        <div className="cg__action-search">
          <span>
            <MdSearch />
          </span>
          <input
            type="text"
            placeholder="Search"
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* <p style={{ fontSize: "18px" }}>
        Programme: {programme && <span>{programme.name}</span>}
      </p> */}

      <p>Please select a Cohort to choose Reports you want to export.</p>

      <ServerSideCohortDataTable
        api={ApiCohort.pagingSearch}
        search={search}
        onSelectedRowsChange={({ selectedRows }) =>
          setSelectedCohorts(selectedRows)
        }
        orderBy={orderBy}
        orderDirection={orderDirection}
        onSort={onSort}
        columns={[
          {
            id: "name",
            name: "Cohort Name",
            sortable: true,
            selector: (row) => row.name,
          },
          {
            id: "startDate",
            name: "Start Date",
            sortable: true,
            cell: (row) =>
              new Date(row.startDate).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              }),
          },
          {
            id: "endDate",
            name: "End Date",
            sortable: true,
            cell: (row) =>
              new Date(row.endDate).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              }),
          },
          {
            id: "description",
            name: "Description",
            sortable: true,
            cell: (row) => <TextEllipsis>{row.description}</TextEllipsis>,
            minWidth: "200px",
            maxWidth: "200px",
          },
          {
            id: "action",
            name: "",
            cell: (row, rowIndex) => (
              <div className="manage-cohorts-link-wrapper">
                <Dropdown align={"end"} drop={"down"}>
                  <Dropdown.Toggle
                    variant="secondary"
                    id="dropdown-basic"
                    className="btn-sm"
                  >
                    Export
                  </Dropdown.Toggle>

                  <Dropdown.Menu
                    renderOnMount
                    popperConfig={{ strategy: "fixed" }}
                  >
                    <button
                      className="dropdown-item"
                      onClick={() => exportData(row)}
                    >
                      {exporting.Summary ? (
                        <Spinner size="sm" variant="info" />
                      ) : (
                        <MdModeEditOutline className="cursor-pointer icon-action-default" />
                      )}{" "}
                      Summary
                    </button>

                    <Dropdown.Divider />

                    <button
                      className="dropdown-item"
                      onClick={() => exportData(row, SUBMISSION_WL)}
                    >
                      {exporting[SUBMISSION_WL] ? (
                        <Spinner size="sm" variant="info" />
                      ) : (
                        <MdModeEditOutline className="cursor-pointer icon-action-default" />
                      )}{" "}
                      Workplace Learning Report
                    </button>

                    {row.pdMode && (
                      <>
                        <Dropdown.Divider />

                        <button
                          className="dropdown-item"
                          onClick={() => exportData(row, SUBMISSION_PD)}
                        >
                          {exporting[SUBMISSION_PD] ? (
                            <Spinner size="sm" variant="info" />
                          ) : (
                            <MdModeEditOutline className="cursor-pointer icon-action-default" />
                          )}{" "}
                          Professional Development Report
                        </button>
                      </>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            ),
            // cell: (row) => {
            //   const from = ROUTES.PORTAL_EXPORT_REPORTS;
            //   return (
            //     <div className="manage-cohorts-link-wrapper">
            //       <Link
            //         to={`${ROUTES.PORTAL_EXPORT_REPORTS}/select-cohort/${programmeId}/${row._id}`}
            //         className="manage-cohorts-link"
            //         state={{ from: from }}
            //       >
            //         Select
            //       </Link>
            //     </div>
            //   );
            // },
          },
        ]}
        refresh={refresh}
        programmeId={programmeId}
        setProgramme={setProgramme}
      />

      <FooterBoxAction onBackUrlClick={back} />
    </main>
  );
}
